import { getDeviceId } from "./deviceId";
// utils/deviceInfo.js

/**
 * Get browser information
 */
export const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browserName = "Unknown";

  if (ua.indexOf("Chrome") > -1 && ua.indexOf("Edg") === -1 && ua.indexOf("OPR") === -1) {
    browserName = "Chrome";
  } else if (ua.indexOf("Edg") > -1) {
    browserName = "Edge";
  } else if (ua.indexOf("OPR") > -1 || ua.indexOf("Opera") > -1) {
    browserName = "Opera";
  } else if (ua.indexOf("Firefox") > -1) {
    browserName = "Firefox";
  } else if (ua.indexOf("Safari") > -1 && ua.indexOf("Chrome") === -1) {
    browserName = "Safari";
  }

  return browserName;
};

/**
 * Get OS information - FIXED: Proper Windows 11 detection
 */
export const getOSInfo = () => {
  const ua = navigator.userAgent;
  let osName = "Unknown";
  let osVersion = "Unknown";

  // Check for Windows (with version detection)
  if (ua.indexOf("Windows NT") > -1) {
    osName = "Windows";
    
    // Extract Windows version from user agent
    const match = ua.match(/Windows NT (\d+\.\d+)/);
    if (match) {
      const ntVersion = match[1];
      
      // Map NT version to Windows version
      const versionMap = {
        '10.0': '11',  // Windows 11 also reports as NT 10.0
        '6.3': '8.1',
        '6.2': '8',
        '6.1': '7',
        '6.0': 'Vista',
        '5.2': 'XP x64',
        '5.1': 'XP',
        '5.0': '2000',
      };
      
      // Check for Windows 11 specifically
      // Windows 11 has NT 10.0 but different build number
      if (ntVersion === '10.0') {
        // Check for Windows 11 by looking at build number
        const buildMatch = ua.match(/Windows NT 10\.0; Win64; x64; ([0-9]+)/);
        if (buildMatch) {
          const buildNumber = parseInt(buildMatch[1]);
          // Windows 11 build numbers start from 22000
          if (buildNumber >= 22000) {
            osVersion = '11';
          } else {
            osVersion = '10';
          }
        } else {
          // Fallback: Check for Windows 11 specific keywords
          if (ua.indexOf('Windows 11') > -1) {
            osVersion = '11';
          } else {
            osVersion = '10';
          }
        }
      } else {
        osVersion = versionMap[ntVersion] || ntVersion;
      }
    }
  } 
  // Check for macOS
  else if (ua.indexOf("Mac OS X") > -1) {
    osName = "macOS";
    const match = ua.match(/Mac OS X (\d+_\d+_\d+)/);
    if (match) {
      const version = match[1].replace(/_/g, ".");
      // Map macOS version to name
      const macVersionMap = {
        '10.15': 'Catalina',
        '11.0': 'Big Sur',
        '12.0': 'Monterey',
        '13.0': 'Ventura',
        '14.0': 'Sonoma',
        '15.0': 'Sequoia',
      };
      // Extract major version
      const majorVersion = version.split('.')[0] + '.' + version.split('.')[1];
      osVersion = macVersionMap[majorVersion] || version;
    } else {
      osVersion = "Unknown";
    }
  } 
  // Check for Android
  else if (ua.indexOf("Android") > -1) {
    osName = "Android";
    const match = ua.match(/Android (\d+\.\d+)/);
    osVersion = match ? match[1] : "Unknown";
  } 
  // Check for iOS
  else if (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1) {
    osName = "iOS";
    const match = ua.match(/OS (\d+_\d+)/);
    osVersion = match ? match[1].replace(/_/g, ".") : "Unknown";
  } 
  // Check for Linux
  else if (ua.indexOf("Linux") > -1 && ua.indexOf("Android") === -1) {
    osName = "Linux";
    osVersion = "Unknown";
  }

  return { name: osName, version: osVersion };
};

/**
 * Get device type
 */
export const getDeviceType = () => {
  const ua = navigator.userAgent;
  
  // Check for tablet first
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "Tablet";
  }
  
  // Check for mobile (phones)
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "Mobile";
  }
  
  // Default to Desktop for everything else (PC, Mac, etc.)
  return "Desktop";
};

/**
 * Get network type - Must be one of: wifi, cellular, ethernet, bluetooth, unknown
 */
export const getNetworkType = () => {
  // Check if user is offline
  if (!navigator.onLine) {
    return "unknown";
  }
  
  // Try to get network info from Network Information API
  if (navigator.connection) {
    const connection = navigator.connection;
    
    // Check connection.type first (more reliable)
    if (connection.type) {
      const typeMap = {
        'wifi': 'wifi',
        'cellular': 'cellular',
        'ethernet': 'ethernet',
        'bluetooth': 'bluetooth',
        'none': 'unknown',
        'unknown': 'unknown',
      };
      
      if (typeMap[connection.type]) {
        return typeMap[connection.type];
      }
    }
    
    // Fallback to effectiveType for cellular detection
    if (connection.effectiveType) {
      const cellularTypes = ['slow-2g', '2g', '3g', '4g', '5g'];
      if (cellularTypes.includes(connection.effectiveType)) {
        return 'cellular';
      }
      
      // If effectiveType is 'wifi' or something else
      if (connection.effectiveType === 'wifi') {
        return 'wifi';
      }
    }
    
    // Additional check for bluetooth
    if (connection.type === 'bluetooth') {
      return 'bluetooth';
    }
  }
  
  // Default fallback - assume wifi
  return 'wifi';
};

/**
 * Get device ID (persistent)
 */

/**
 * Get complete device data for API
 */
export const getDeviceData = (fcmToken = null) => {
  const os = getOSInfo();
  const deviceType = getDeviceType();
  const networkType = getNetworkType();
  const deviceId = getDeviceId();

  const deviceData = {
    device_id: deviceId,
    device_name: deviceType,
    device_model: deviceType,
    manufacturer: "Web",
    os_version: `${os.name} ${os.version}`.trim(),
    os_build: os.version,
    sdk_int: 0,
    app_version: process.env.REACT_APP_VERSION || "1.0.0",
    app_build_number: process.env.REACT_APP_BUILD || "100",
  };

  // Only add network_type if it's not null or undefined
  if (networkType) {
    deviceData.network_type = networkType;
  }

  // Only add fcm_token if provided and not empty
  if (fcmToken && fcmToken.trim() !== '') {
    deviceData.fcm_token = fcmToken;
  }

  return deviceData;
};

/**
 * Validate network type against allowed values
 */
export const isValidNetworkType = (networkType) => {
  const allowedTypes = ['wifi', 'cellular', 'ethernet', 'bluetooth', 'unknown'];
  return allowedTypes.includes(networkType);
};

export default {
  getBrowserInfo,
  getOSInfo,
  getDeviceType,
  getNetworkType,
  getDeviceId,
  getDeviceData,
  isValidNetworkType,
};